import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContactStatus, ConsentTracking } from '@dev/ee-api/ee-api-models';
import { SimplePopover, Typography } from '@dev/ee-components';
import { Grid } from '@material-ui/core';
import styled from 'styled-components';

import { prepareStatusInfo } from '@/components/Contacts/helperFunctions';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { ContactSettingsProps } from '../../../ContactsUpload';
import { AssignContactsListsSelect } from '../ContactListSelect';

const ContactSettingsContainer = styled(Grid)`
    input {
        padding: 0;
        border: 0; //prevent 2018 styles
    }
`;

const useContactStatuses = (): { value: ContactStatus; label: string }[] => {
    const { t } = useTranslation();
    const { audience: { isAudienceTransactionalContactStatusVisible } } = useDashboardElementVisibility();

    const statuses = [
        {
            value: ContactStatus.Transactional,
            label: t('transactional'),
        },
        {
            value: ContactStatus.Active,
            label: t('active'),
        },
        {
            value: ContactStatus.Inactive,
            label: t('inactive'),
        },
        {
            value: ContactStatus.Unsubscribed,
            label: t('unsubscribed'),
        },
        {
            value: ContactStatus.Abuse,
            label: t('complaint'),
        },
    ];

    return statuses.filter((status) => {
        if (status.value === ContactStatus.Transactional) {
            return isAudienceTransactionalContactStatusVisible;
        }
        return true;
    });
};

export const ContactSettings = (props: ContactSettingsProps): JSX.Element => {
    const { t } = useTranslation();
    const contactStatuses = useContactStatuses();
    const {
        createList,
        listName,
        setListName,
        isValidListName,
        setValidListName,
        status,
        setStatus,
        setConsentTracking,
        consentTracking,
    } = props;
    const contactStatusInfo = prepareStatusInfo(status);

    const getTrackingConsent = (): { value: ConsentTracking; label: string }[] => [
        {
            value: ConsentTracking.Unknown,
            label: t('trackingunknown'),
        },
        {
            value: ConsentTracking.Allow,
            label: t('trackingallow'),
        },
        {
            value: ConsentTracking.Deny,
            label: t('trackingdeny'),
        },
    ];

    const handleUploadedStatus = (e, obj): void => {
        setStatus(obj.value as string);
    };

    const handleConsentTracking = (e, obj): void => {
        setConsentTracking(obj.value as ConsentTracking);
    };

    return (
        <ContactSettingsContainer container spacing={4}>
            <AssignContactsListsSelect
                createList={createList}
                listName={listName}
                setListName={setListName}
                isValidListName={isValidListName}
                setValidListName={setValidListName}
            />
            <Grid item xs={12} md={6}>
                <SimplePopover
                    id="upload-statuses"
                    options={contactStatuses}
                    type="input"
                    fullWidth
                    label={t('setuploadedstatus')}
                    value={status}
                    onItemClick={handleUploadedStatus}
                />
                <Typography variant="body1" bodyVariant="help">
                    {contactStatusInfo}
                </Typography>
            </Grid>
            {status !== ContactStatus.Inactive &&
                status !== ContactStatus.Unsubscribed &&
                status !== ContactStatus.Abuse && (
                    <Grid item xs={12} md={6}>
                        <SimplePopover
                            id="tracking-consent"
                            label={t('trackingconsent')}
                            type="input"
                            fullWidth
                            options={getTrackingConsent()}
                            value={consentTracking}
                            onItemClick={handleConsentTracking}
                        />
                    </Grid>
                )}
        </ContactSettingsContainer>
    );
};
